import { useState } from "react";
import { ErrorCodes, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import {
  CODE_PATTERN,
  COVERAGE_ROLES,
  PREEXISTING_HANDLINGS,
  RENEWAL_GUARANTEES,
  type CoverageRole,
  type CreatePackageInput,
  type PackageCoverageInput,
  type PackageVersionInput,
  type PreexistingHandling,
  type RenewalGuarantee,
} from "../../api/catalog-contracts";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  StatusLabel,
  Table,
  TableCard,
} from "../../ui";
import { useCatalogLines } from "./use-catalog-mgmt";
import {
  useAddPackageVersion,
  useCreatePackage,
  usePackage,
  usePackagesByLine,
} from "./use-product-catalog";

// Packages management (catalog step 2, ADR 0004 §5). A package is the quotable unit —
// the SAME feed the console's "Şirket — Paket" dropdown reads. A package version PINS
// its coverage-set (open coverage versions resolved + frozen at creation). Listing is
// the org-gated distributable read (GET /packages?line=); detail (GET /packages/:code)
// shows the pinned, frozen coverage-set. Creation and version-append pin a fresh set.
// RBAC (catalog:write @ all) is UX; the API enforces regardless.

export function PackageManagement({ canWrite }: { canWrite: boolean }) {
  const { t } = useT();
  const lines = useCatalogLines();
  const [line, setLine] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const pkgs = usePackagesByLine(line || null);

  return (
    <section className="surf-stack" data-testid="pkg-panel">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("pkg.heading")}</h1>
          <p className="surf-sub muted">{t("pkg.governedNote")}</p>
        </div>
      </div>

      <TableCard
        title={t("pkg.list.title")}
        actions={
          <div className="surf-filters">
            <Select
              label={t("cat.auth.line")}
              value={line}
              onChange={(e) => {
                setLine(e.target.value);
                setSelected(null);
              }}
            >
              <option value="">{t("pkg.pickLine")}</option>
              {(lines.data?.data ?? []).map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </Select>
            {lines.error != null && (
              <p className="form-error" role="alert" data-testid="lines-load-failed">
                {t("cat.linesLoadFailed")}
              </p>
            )}
          </div>
        }
      >
        {line === "" && (
          <EmptyState data-testid="pkg-pickline" icon="records" title={t("pkg.pickLinePrompt")} />
        )}
        {line !== "" && pkgs.isLoading && <LoadingState label={t("pkg.loading")} rows={3} />}
        {pkgs.error instanceof ProblemError && (
          <ErrorState title={t("pkg.error")} description={pkgs.error.problem.detail} />
        )}
        {line !== "" && pkgs.data && pkgs.data.data.length === 0 && (
          <EmptyState data-testid="pkg-empty" icon="records" title={t("pkg.empty")} />
        )}
        {line !== "" && pkgs.data && pkgs.data.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("cat.list.code")}</th>
                <th>{t("pkg.list.name")}</th>
                <th>{t("pkg.list.carrier")}</th>
                <th className="cat-td-right" />
              </tr>
            </thead>
            <tbody>
              {pkgs.data.data.map((p) => (
                <tr key={p.packageCode} data-testid="pkg-row">
                  <td>
                    <code>{p.packageCode}</code>
                  </td>
                  <td className="cat-strong">{p.packageName}</td>
                  <td>{p.carrierTradeName}</td>
                  <td className="cat-td-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      data-testid="pkg-detail-btn"
                      onClick={() =>
                        setSelected((s) => (s === p.packageCode ? null : p.packageCode))
                      }
                    >
                      {t("cat.detail")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      {selected && <PackageDetail code={selected} canWrite={canWrite} />}
      <AddPackageForm canWrite={canWrite} onCreated={setSelected} />
    </section>
  );
}

function PackageDetail({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const pkg = usePackage(code);

  return (
    <div className="cat-detail surf-stack" data-testid="pkg-detail">
      <div className="cat-detail-head">
        <span className="cat-detail-eyebrow muted">{t("pkg.detailEyebrow")}</span>
        <h2 className="cat-detail-name">
          {pkg.data ? pkg.data.current.name : code} <code>{code}</code>
        </h2>
      </div>

      {pkg.isLoading && <LoadingState label={t("pkg.loading")} rows={2} />}
      {pkg.error instanceof ProblemError && (
        <p role="alert" className="form-error">
          {pkg.error.problem.detail}
        </p>
      )}
      {pkg.data === null && (
        <EmptyState data-testid="pkg-notfound" icon="records" title={t("pkg.notFound")} />
      )}
      {pkg.data && (
        <TableCard
          title={t("pkg.pinned.title")}
          footer={<span className="muted surf-foot-note">{t("pkg.pinned.frozenNote")}</span>}
        >
          {pkg.data.coverages.length === 0 ? (
            <EmptyState icon="records" title={t("pkg.pinned.empty")} />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("cat.list.code")}</th>
                  <th>{t("cov.name")}</th>
                  <th>{t("pkg.pinned.role")}</th>
                  <th>{t("pkg.pinned.pinnedVersion")}</th>
                  <th>{t("pkg.pinned.limit")}</th>
                </tr>
              </thead>
              <tbody>
                {pkg.data.coverages.map((c) => (
                  <tr key={c.coverageCode} data-testid="pkg-pinned-row">
                    <td>
                      <code>{c.coverageCode}</code>
                    </td>
                    <td className="cat-strong">{c.name}</td>
                    <td>
                      <StatusLabel tone={c.role === "CORE" ? "active" : "expiring"}>
                        {t(`pkg.role.${c.role}`)}
                      </StatusLabel>
                    </td>
                    <td>v{c.coverageVersion}</td>
                    <td className="rate-num">
                      {c.limitAmountOverride ?? c.limitAmount ?? t("pkg.pinned.unlimited")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableCard>
      )}

      <PackageVersionForm code={code} canWrite={canWrite} />
    </div>
  );
}

// The shared package-attribute + coverage-set fieldset. Both create (with identity) and
// append-version reuse it — a version re-pins a FRESH coverage-set (never a patch).
function usePackageForm() {
  const [name, setName] = useState("");
  const [renewalGuarantee, setRenewalGuarantee] = useState<RenewalGuarantee>("NONE");
  const [preexistingHandling, setPreexistingHandling] =
    useState<PreexistingHandling>("NOT_APPLICABLE");
  const [minEntryAge, setMinEntryAge] = useState("");
  const [maxEntryAge, setMaxEntryAge] = useState("");
  const [networkCode, setNetworkCode] = useState("");
  const [coverages, setCoverages] = useState<PackageCoverageInput[]>([
    { coverageCode: "", role: "CORE" },
  ]);

  function attributes(): PackageVersionInput {
    const clean = coverages
      .filter((c) => c.coverageCode.trim() !== "")
      .map((c) => ({ coverageCode: c.coverageCode.trim(), role: c.role }));
    const body: PackageVersionInput = {
      name: name.trim(),
      renewalGuarantee,
      preexistingHandling,
      coverages: clean,
    };
    if (minEntryAge.trim()) body.minEntryAge = Number(minEntryAge);
    if (maxEntryAge.trim()) body.maxEntryAge = Number(maxEntryAge);
    if (networkCode.trim()) body.networkCode = networkCode.trim();
    return body;
  }

  const validCoverages = coverages.filter((c) => c.coverageCode.trim() !== "").length;
  const filled = name.trim() !== "" && validCoverages >= 1;

  return {
    name,
    setName,
    renewalGuarantee,
    setRenewalGuarantee,
    preexistingHandling,
    setPreexistingHandling,
    minEntryAge,
    setMinEntryAge,
    maxEntryAge,
    setMaxEntryAge,
    networkCode,
    setNetworkCode,
    coverages,
    setCoverages,
    attributes,
    filled,
  };
}

function PackageAttributeFields({
  form,
  disabled,
}: {
  form: ReturnType<typeof usePackageForm>;
  disabled: boolean;
}) {
  const { t } = useT();
  return (
    <>
      <fieldset className="k-form-grid" disabled={disabled}>
        <Input
          label={t("pkg.name")}
          value={form.name}
          onChange={(e) => form.setName(e.target.value)}
        />
        <Select
          label={t("pkg.renewal")}
          value={form.renewalGuarantee}
          onChange={(e) => form.setRenewalGuarantee(e.target.value as RenewalGuarantee)}
        >
          {RENEWAL_GUARANTEES.map((r) => (
            <option key={r} value={r}>
              {t(`pkg.renewal.${r}`)}
            </option>
          ))}
        </Select>
        <Select
          label={t("pkg.preexisting")}
          value={form.preexistingHandling}
          onChange={(e) => form.setPreexistingHandling(e.target.value as PreexistingHandling)}
        >
          {PREEXISTING_HANDLINGS.map((p) => (
            <option key={p} value={p}>
              {t(`pkg.preexisting.${p}`)}
            </option>
          ))}
        </Select>
        <Input
          label={t("pkg.minEntryAge")}
          type="number"
          value={form.minEntryAge}
          onChange={(e) => form.setMinEntryAge(e.target.value)}
        />
        <Input
          label={t("pkg.maxEntryAge")}
          type="number"
          value={form.maxEntryAge}
          onChange={(e) => form.setMaxEntryAge(e.target.value)}
        />
        <Input
          label={t("pkg.networkCode")}
          hint={t("pkg.networkHint")}
          value={form.networkCode}
          onChange={(e) => form.setNetworkCode(e.target.value)}
        />
      </fieldset>

      <CoverageSetEditor form={form} disabled={disabled} />
    </>
  );
}

// The pinned coverage-set editor — min 1 row. Each row: a coverage code (looked up to
// its CURRENT version + frozen server-side) and its role in the package.
function CoverageSetEditor({
  form,
  disabled,
}: {
  form: ReturnType<typeof usePackageForm>;
  disabled: boolean;
}) {
  const { t } = useT();
  const set = (i: number, patch: Partial<PackageCoverageInput>) =>
    form.setCoverages((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="pkg-covset" data-testid="pkg-covset">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("pkg.covset.title")}</span>
        <span className="cat-append-pill">
          <Icon name="lock" size={12} strokeWidth={1.9} />
          {t("pkg.covset.pinTag")}
        </span>
      </div>
      <p className="muted surf-note">{t("pkg.covset.note")}</p>

      {form.coverages.map((row, i) => (
        <fieldset className="k-form-grid pkg-covrow" disabled={disabled} key={i}>
          <Input
            label={t("pkg.covset.coverageCode")}
            placeholder="allianz-ayakta-tedavi"
            value={row.coverageCode}
            onChange={(e) => set(i, { coverageCode: e.target.value })}
          />
          <Select
            label={t("pkg.covset.role")}
            value={row.role}
            onChange={(e) => set(i, { role: e.target.value as CoverageRole })}
          >
            {COVERAGE_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`pkg.role.${r}`)}
              </option>
            ))}
          </Select>
          <div className="pkg-covrow-actions">
            {form.coverages.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                disabled={disabled}
                onClick={() => form.setCoverages((rows) => rows.filter((_, idx) => idx !== i))}
              >
                {t("pkg.covset.remove")}
              </Button>
            )}
          </div>
        </fieldset>
      ))}
      <div className="k-form-actions">
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={disabled}
          data-testid="pkg-covset-add"
          onClick={() =>
            form.setCoverages((rows) => [...rows, { coverageCode: "", role: "OPTIONAL" }])
          }
        >
          {t("pkg.covset.addRow")}
        </Button>
      </div>
    </div>
  );
}

function PackageVersionForm({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const add = useAddPackageVersion(code);
  const form = usePackageForm();
  const canSubmit = canWrite && form.filled && !add.isPending;

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("pkg.version.add")}</span>
        <span className="cat-append-pill">{t("cat.appendOnlyTag")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("pkg.version.note")}</p>

      <form
        data-testid="pkg-version-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) add.mutate(form.attributes());
        }}
      >
        <PackageAttributeFields form={form} disabled={!canWrite} />
        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={add.isPending}>
            {t("pkg.version.submit")}
          </Button>
          {add.isSuccess && (
            <span role="status" className="form-ok">
              {t("pkg.version.success")}
            </span>
          )}
          {add.error instanceof ProblemError && (
            <span role="alert" className="form-error">
              {add.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

// Create a package — identity (code + product) + version 1 + pinned coverage-set. A
// duplicate code returns 409; a coverage from a different carrier/line returns 409.
function AddPackageForm({
  canWrite,
  onCreated,
}: {
  canWrite: boolean;
  onCreated: (code: string) => void;
}) {
  const { t } = useT();
  const create = useCreatePackage();
  const form = usePackageForm();
  const [code, setCode] = useState("");
  const [productCode, setProductCode] = useState("");

  const codeInvalid = code.trim() !== "" && !CODE_PATTERN.test(code.trim());
  const canSubmit =
    canWrite &&
    code.trim() !== "" &&
    !codeInvalid &&
    productCode.trim() !== "" &&
    form.filled &&
    !create.isPending;

  function submit() {
    const body: CreatePackageInput = {
      code: code.trim(),
      productCode: productCode.trim(),
      ...form.attributes(),
    };
    create.mutate(body, { onSuccess: (res) => onCreated(res.code) });
  }

  const isConflict = create.error instanceof ProblemError && create.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("pkg.add")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("pkg.governedNote")}</p>

      <form
        data-testid="pkg-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input
            label={t("cat.list.code")}
            placeholder="allianz-tamamlayici-standart"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint={t("pkg.codeHint")}
            error={codeInvalid ? t("cat.carrier.invalidCode") : undefined}
            errorTestId="pkg-err-code"
          />
          <Input
            label={t("pkg.productCode")}
            placeholder="allianz-tamamlayici"
            hint={t("pkg.productCodeHint")}
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
          />
        </fieldset>
        <PackageAttributeFields form={form} disabled={!canWrite} />
        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t("pkg.submit")}
          </Button>
          {create.isSuccess && (
            <span role="status" className="form-ok">
              {t("pkg.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="pkg-conflict">
              {create.error instanceof ProblemError ? create.error.problem.detail : ""}
            </span>
          )}
          {create.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {create.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}
